/**
 * What face the pet is wearing, and why.
 *
 * The Drone carries a small matrix of neon cubes on its carapace, and the
 * expression on it is decided HERE - as a pure function of two facts the client
 * already has authoritatively, so no request is made and nothing new is stored.
 *
 * ## The pet has no health
 *
 * It cannot be hit, blocks nothing and takes no damage - so "the pet is hurt"
 * has no meaning. What the sad face reacts to is the SHIP being hurt, which the
 * simulation already announces as a leak event.
 *
 * ## Priority, not blending
 *
 *     boss on the field            -> ANGRY
 *     no boss, ship just hit       -> SAD
 *     otherwise                    -> HAPPY
 *
 * Ordered rather than combined, and that ordering is the requirement: taking a
 * hit while the boss is descending must not turn anger into sadness. The boss
 * outranks it for as long as the encounter lasts.
 */

export type PetMood = 'happy' | 'sad' | 'angry';

/**
 * How long the sad face lasts after the ship is hit, milliseconds.
 *
 * A fresh hit RENEWS it rather than queueing another one, so a wave of leaks
 * keeps the face down for as long as they keep landing instead of stacking up
 * a sadness debt that outlives the danger.
 */
export const PET_SAD_DURATION_MS = 1200;

export interface PetMoodInput {
  /**
   * True while a boss is on the field, from its reveal to the end of the
   * encounter - defeated, escaped, or the ship died.
   */
  readonly bossActive: boolean;
  /** Milliseconds since the ship last took damage, or null if it never has. */
  readonly msSinceShipDamage: number | null;
}

export function petMood({ bossActive, msSinceShipDamage }: PetMoodInput): PetMood {
  if (bossActive) {
    return 'angry';
  }
  if (
    msSinceShipDamage !== null &&
    msSinceShipDamage >= 0 &&
    msSinceShipDamage < PET_SAD_DURATION_MS
  ) {
    return 'sad';
  }
  return 'happy';
}

/**
 * The three faces, as pixel art.
 *
 * Eleven columns by seven rows, `O` for a lit cube and `.` for nothing. Written
 * as text on purpose: the shape of an expression is something to be read at a
 * glance while editing it, and a list of coordinates is not.
 *
 * It was 7x5, and at that resolution a smile could only be one straight row
 * with two raised ends - which read as a flat line rather than as a curve. The
 * extra columns buy the intermediate steps a curve needs, and the extra rows
 * buy eyes that are blocks instead of dots.
 *
 * Only cells that are lit in AT LEAST ONE face get a cube built, and switching
 * expression toggles visibility. Nothing is created, destroyed or re-uploaded
 * when the mood changes.
 */
export const PET_FACE_COLUMNS = 11;
export const PET_FACE_ROWS = 7;

export const PET_FACE_PATTERNS: Readonly<Record<PetMood, readonly string[]>> = Object.freeze({
  /*
   * Block eyes, and a mouth that genuinely curves.
   *
   * Three steps down each side - ends high at row 4, shoulders at row 5, floor
   * at row 6 - so the corners visibly rise instead of the whole mouth being one
   * row with two stray pixels on the end.
   */
  happy: Object.freeze([
    '...........',
    '..OO...OO..',
    '..OO...OO..',
    '...........',
    'O.........O',
    '.OO.....OO.',
    '...OOOOO...',
  ]),
  // The same eyes; the mouth is the exact vertical mirror of the smile.
  sad: Object.freeze([
    '...........',
    '..OO...OO..',
    '..OO...OO..',
    '...........',
    '...OOOOO...',
    '.OO.....OO.',
    'O.........O',
  ]),
  /*
   * Brows driving down toward the middle, narrowed eyes beneath them, a hard
   * flat mouth and two fangs. Nothing about it can be mistaken for the others.
   */
  angry: Object.freeze([
    'OO.......OO',
    '.OO.....OO.',
    '..OO...OO..',
    '..OO...OO..',
    '...........',
    '..OOOOOOO..',
    '...O...O...',
  ]),
});

/** Every cell any face uses, so the matrix is built once. */
export function petFaceCells(): readonly { readonly row: number; readonly column: number }[] {
  const cells: { row: number; column: number }[] = [];
  for (let row = 0; row < PET_FACE_ROWS; row += 1) {
    for (let column = 0; column < PET_FACE_COLUMNS; column += 1) {
      const lit = Object.values(PET_FACE_PATTERNS).some(
        (pattern) => pattern[row]?.[column] === 'O',
      );
      if (lit) {
        cells.push({ row, column });
      }
    }
  }
  return cells;
}

/** Whether one cell is lit in one expression. */
export function isFaceCellLit(mood: PetMood, row: number, column: number): boolean {
  return PET_FACE_PATTERNS[mood][row]?.[column] === 'O';
}
